import type { PresentationReviewMemo } from "@/api/types";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/**
 * Renders the memo half of the presentation review surface (issue
 * #144). Every field is rendered via React text-interpolation —
 * NEVER `dangerouslySetInnerHTML`, NEVER through a markdown renderer
 * that allows HTML — because the upstream chatbot visitor controls
 * every value. See
 * `docs/decisions/2026-06-15-admin-ui-xss-safe-rendering.md` and the
 * runtime test in `tests/unit/MemoDisplay.test.tsx`.
 *
 * Empty arrays and null strings render as a muted "Not provided"
 * placeholder rather than collapsing the section — the operator
 * needs to see WHICH fields the visitor left blank.
 */
export interface MemoDisplayProps {
  memo: PresentationReviewMemo;
}

export function MemoDisplay({ memo }: MemoDisplayProps) {
  const driveUrl = memo.driveFileId
    ? `https://docs.google.com/document/d/${memo.driveFileId}`
    : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Memo</CardTitle>
        <CardDescription>
          Client intake snapshot · ID {memo.shortId}
          {driveUrl && (
            <>
              {" · "}
              <a
                className="underline text-blue-600 hover:text-blue-800"
                href={driveUrl}
                rel="noopener noreferrer"
                target="_blank"
              >
                Open Drive doc
              </a>
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <MemoField label="Mission" value={memo.mission} />
        <MemoField label="Vision" value={memo.vision} />
        <MemoListField label="Objectives" values={memo.objectives} />
        <MemoField label="Value added" value={memo.valueAdded} />
        <MemoListField label="Bottlenecks" values={memo.bottlenecks} />
        <BudgetField budget={memo.budget} />
        <MemoListField label="Assumptions" values={memo.assumptions} />
        <MemoListField label="Tech stack" values={memo.techStack} />
        <MemoField label="Current workflow" value={memo.currentWorkflow} />
        <MemoField label="Decision maker" value={memo.decisionMaker} />
        <MemoField label="Urgency" value={memo.urgency} />
      </CardContent>
    </Card>
  );
}

interface MemoFieldProps {
  label: string;
  value: string | null;
}

function MemoField({ label, value }: MemoFieldProps) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {value && value.trim().length > 0 ? (
        // React's text-interpolation escapes HTML automatically — the
        // {value} below renders <script>…</script> as literal text,
        // not as a script tag. This is the load-bearing property the
        // XSS test asserts.
        <div className="mt-1 whitespace-pre-wrap text-sm">{value}</div>
      ) : (
        <div className="mt-1 text-sm text-muted-foreground italic">Not provided</div>
      )}
    </div>
  );
}

interface BudgetFieldProps {
  budget: PresentationReviewMemo["budget"];
}

/**
 * Render the structured budget (issue #573) as text — labelled range
 * when the client volunteered more than one number, single-value with
 * label otherwise. Currency symbol via `Intl.NumberFormat` seeded with
 * the memo's ISO 4217 code; falls back to a bare number when currency
 * is null (legacy or hand-inserted rows). Whole-currency amounts only
 * (no fractional pence/cents) — matches how clients state budgets in
 * practice and mirrors the memo-doc renderer in
 * `packages/consultant/src/memo-doc.ts::formatBudget`.
 *
 * Every numeric arm is served as a numeric-as-string by Drizzle;
 * `Number.parseFloat` is intentionally lenient (garbage → "Not
 * provided" via the `finite` check) so a hand-edited row can't crash
 * the render. Text-interpolation still handles all string values, so
 * XSS surface matches the sibling `MemoField` component.
 */
function BudgetField({ budget }: BudgetFieldProps) {
  const label = "Budget";
  const rendered = formatMemoBudget(budget);
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {rendered !== null ? (
        <div className="mt-1 whitespace-pre-wrap text-sm">{rendered}</div>
      ) : (
        <div className="mt-1 text-sm text-muted-foreground italic">Not provided</div>
      )}
    </div>
  );
}

/** Exported for the unit test that anchors #573's rendering rules. */
export function formatMemoBudget(
  budget: PresentationReviewMemo["budget"],
): string | null {
  const min = parseNumericString(budget.min);
  const target = parseNumericString(budget.target);
  const max = parseNumericString(budget.max);
  if (min === null && target === null && max === null) return null;

  const fmt = (n: number): string => {
    if (budget.currency === null) {
      // Legacy / hand-inserted row — render the number without a
      // symbol rather than guessing a currency that could be wrong.
      return Math.round(n).toLocaleString("en-GB");
    }
    try {
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: budget.currency,
        maximumFractionDigits: 0,
      }).format(n);
    } catch {
      // Unknown ISO 4217 code (should never happen — the Consultant
      // schema constrains to A-Z{3} — but defend the render path).
      return `${Math.round(n).toLocaleString("en-GB")} ${budget.currency}`;
    }
  };

  // Special case: floor + ceiling with no explicit target renders
  // as a hyphenated range for readability ("£300 – £500").
  if (min !== null && max !== null && target === null) {
    return `${fmt(min)} – ${fmt(max)}`;
  }
  const parts: string[] = [];
  if (min !== null) parts.push(`${fmt(min)} min`);
  if (target !== null) parts.push(`${fmt(target)} target`);
  if (max !== null) parts.push(`${fmt(max)} max`);
  return parts.join(", ");
}

function parseNumericString(raw: string | null): number | null {
  if (raw === null) return null;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : null;
}

interface MemoListFieldProps {
  label: string;
  values: readonly string[];
}

function MemoListField({ label, values }: MemoListFieldProps) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </div>
      {values.length > 0 ? (
        <ul className="mt-1 list-disc pl-5 space-y-1 text-sm">
          {values.map((v, idx) => (
            // Same text-interpolation guarantee — `<li>{v}</li>`
            // renders the string as text, not as parsed HTML.
            <li key={`${label}-${idx}`}>{v}</li>
          ))}
        </ul>
      ) : (
        <div className="mt-1 text-sm text-muted-foreground italic">Not provided</div>
      )}
    </div>
  );
}
